/**
 * Resource Admin Page
 * APP_ADMIN only page for managing resource types, resource catalog, and client resource configurations
 */

import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import {
  Category as CategoryIcon,
  ViewList as ViewListIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import ResourceTypesTab from './tabs/ResourceTypesTab';
import ResourceCatalogTab from './tabs/ResourceCatalogTab';
import ClientResourceConfigTab from './tabs/ClientResourceConfigTab';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Resource Admin Page Component
 * Main page for managing v5 resource system with three tabs
 */
function ResourceAdmin() {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{ p: 3 }}>
          {/* Page Header */}
          <Box mb={4}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Resource Administration
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab
                icon={<CategoryIcon />}
                iconPosition="start"
                label="Resource Types"
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
              <Tab
                icon={<ViewListIcon />}
                iconPosition="start"
                label="Resource Catalog"
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
              <Tab
                icon={<SettingsIcon />}
                iconPosition="start"
                label="Client Configuration"
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <Box>
            {tabValue === 0 && <ResourceTypesTab />}
            {tabValue === 1 && <ResourceCatalogTab />}
            {tabValue === 2 && <ClientResourceConfigTab />}
          </Box>
        </Box>
      </DashboardLayout>
    </QueryClientProvider>
  );
}

export default ResourceAdmin;
